import React from "react";
import { withStyles } from "material-ui/styles";
import { connect } from "react-redux";
import { compose } from "recompose";
import Typography from "material-ui/Typography";
import Paper from "material-ui/Paper";
import Button from "material-ui/Button";
import Icon from "material-ui/Icon";
import Tooltip from "material-ui/Tooltip";
import Radio from "material-ui/Radio";
import Title from "../ui/title";
import Content from "../ui/content";
import Back from "../ui/back";
import SearchBar from "../common/search";
import Table from "../table";
import TestsRowLink from "../table/cells/testsRowLink";
import Context from "../devices/context";
import { dateTimeFormatter } from "../../common/utils";
import { Heart } from "lucide-react";
import HealthCheckModal from "../devices/modals/healthCheckModal";
import ConfirmDialog from "../common/dialog/confirm";

const safeTranslate = (translate, key, fallback) =>
  typeof translate === "function" ? translate(key) : fallback;

const styles = theme => ({
  bodyScroll: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  tableRegion: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    '& .ReactTable': {
      flex: '1 1 auto',
      minHeight: 0,
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    },
    '& .ReactTable .rt-table': {
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
      marginBottom: 0
    },
    '& .ReactTable .rt-thead': {
      flex: '0 0 auto',
      background: '#fff'
    },
    '& .ReactTable .rt-tbody': {
      padding: '0 !important',
      overflowY: 'auto',
      overflowX: 'hidden'
    },
    '& .ReactTable .rt-tbody .rt-tr-group': {
      flex: '0 0 auto'
    },
    '& .ReactTable .rt-tbody .rt-tr': {
      flex: '0 0 auto'
    },
    '& .ReactTable .rt-td': {
      lineHeight: '1.4 !important',
      padding: '8px 6px !important'
    },
    '& .ReactTable .rt-td a': {
      lineHeight: '1.4 !important'
    },
    '& .ReactTable .rt-td p': {
      lineHeight: '1.4 !important'
    },
    '& .ReactTable .-pagination': {
      position: 'relative',
      flex: '0 0 auto',
      width: '100%',
      marginBottom: 0,
      backgroundColor: '#fff'
    }
  },
  emptyState: {
    padding: "16px 0"
  },
  healthIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%'
  },
  healthIconClickable: {
    cursor: 'pointer'
  },
  button: {
    margin: theme.spacing.unit,
    background: theme.palette.accent,
    '&:focus': {
      outline: 'none'
    },
    '&:hover': {
      backgroundColor: '#fd5622'
    }
  }
});

const formatDateTime = value => (value ? dateTimeFormatter(value) : "-");
const formatValue = value => (value === null || typeof value === "undefined" || value === "" ? "-" : value);
const formatTipWearCounter = value => (value === null || typeof value === "undefined" || value === "" || value === 0 || value === "0" ? "-" : value);

const hasRole = (roles, role) => {
  const roleList = Array.isArray(roles) ? roles : String(roles || "").split(",");
  return roleList.some(value => String(value).trim().toLowerCase() === role);
};

const supportsAdapterMetadata = adapter => {
  const typeText = `${adapter.adaptertypename || ""} ${adapter.moduletype || ""}`.toLowerCase();
  return /tier[\s-]*1|fiber\s*tek/.test(typeText);
};

const getAdapterTipWearCategory = adapter => {
  const typeText = `${adapter.adaptertypename || ""} ${adapter.moduletype || ""}`.toLowerCase();

  if (/patch/.test(typeText)) {
    return "PatchAdapter";
  }

  if (/permanent\s*link|\bpl\b/.test(typeText)) {
    return "PLA";
  }

  return "Default";
};

const getAdapterTipWearThreshold = (adapter, tipWearThresholds) => {
  const thresholds = tipWearThresholds || {};
  const category = getAdapterTipWearCategory(adapter);

  return thresholds[category] || thresholds.Default || 4500;
};

const getDateOnly = value => new Date(value.getFullYear(), value.getMonth(), value.getDate());

const computeAdapterHealth = (adapter, tipWearThresholds) => {
  if (!supportsAdapterMetadata(adapter)) return null;

  const tipWearThreshold = getAdapterTipWearThreshold(adapter, tipWearThresholds);
  const today = getDateOnly(new Date());

  if (!adapter.calibrationduedate) {
    return "red";
  }

  const due = getDateOnly(new Date(adapter.calibrationduedate));
  if (Number.isNaN(due.getTime())) {
    return "red";
  }

  const daysUntilCalibrationDue = Math.floor((due - today) / (1000 * 60 * 60 * 24));
  if (daysUntilCalibrationDue < 0) {
    return "red";
  }

  if (daysUntilCalibrationDue <= 45) {
    return "amber";
  }

  let lastActivityFailed = true;
  if (adapter.lastresultdatetime) {
    const last = new Date(adapter.lastresultdatetime);
    const daysSince = (new Date() - last) / (1000 * 60 * 60 * 24);
    lastActivityFailed = daysSince > 90;
  }

  let tipWearFailed = false;
  if (typeof adapter.tipwearcounter === "number" && adapter.tipwearcounter !== null && tipWearThreshold > 0) {
    tipWearFailed = adapter.tipwearcounter > tipWearThreshold;
  }

  return lastActivityFailed || tipWearFailed ? "amber" : "green";
};

const AdapterToolbar = ({ classes, translate, toggle, toggleFilters, viewSwitcher, showDelete, del }) => {
  const translateText = typeof translate === "function" ? translate : value => value;

  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
      {viewSwitcher ? <div style={{ display: "flex", alignItems: "center" }}>{viewSwitcher}</div> : null}
      {showDelete ? (
        <Tooltip title={translateText("tooltip_delete")} disableTriggerFocus={true}>
          <Button aria-label={translateText("tooltip_delete")} className={classes.button} color="primary" fab mini onClick={() => del()}>
            <Icon>delete</Icon>
          </Button>
        </Tooltip>
      ) : null}
      <Tooltip title={translateText("tooltip_filter")} disableTriggerFocus={true}>
        <Button aria-label={translateText("tooltip_filter")} className={classes.button} color="primary" fab mini onClick={() => toggleFilters()}>
          <Icon>filter_list</Icon>
        </Button>
      </Tooltip>
      <Tooltip title={translateText("tooltip_view_columns")} disableTriggerFocus={true}>
        <Button aria-label={translateText("tooltip_view_columns")} className={classes.button} color="primary" fab mini onClick={() => toggle()}>
          <Icon>view_column</Icon>
        </Button>
      </Tooltip>
    </div>
  );
};

class AdapterAssetsPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      contextMenu: false,
      columns: null,
      filterable: false,
      searchPager: {
        pageno: 1
      },
      searchTerm: "",
      showConfirmDelete: false,
      selectedAdapters: [],
      selected: {},
      healthCheckModal: {
        isOpen: false,
        checks: null
      }
    };
  }

  componentWillMount() {
    this.setState({
      columns: this.createColumns(this.props, this.state.filterable)
    });
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.translate !== this.props.translate) {
      const visibility = this.getColumnVisibility(this.state.columns);
      this.setState({
        columns: this.createColumns(nextProps, this.state.filterable, visibility)
      });
    }
  }

  getColumnVisibility = columns => {
    if (!columns) {
      return null;
    }

    return columns.reduce((visibility, column) => {
      visibility[column.id] = column.show;
      return visibility;
    }, {});
  };

  createColumns = (props, filterable, visibility = null) => {
    const show = (columnId, defaultShow) => {
      if (visibility && Object.prototype.hasOwnProperty.call(visibility, columnId)) {
        return visibility[columnId];
      }

      return defaultShow;
    };

    return [
      {
        id: "adapterhealth",
        Header: safeTranslate(props.translate, 'adapter_health', 'Health'),
        accessor: d => {
          const status = computeAdapterHealth(d, props.adapterTipWearThresholds);
          return status || "";
        },
        toggle: false,
        show: true,
        display: true,
        filterable: false,
        cansearch: false,
        sortable: false,
        width: 70,
        Cell: o => {
          const status = computeAdapterHealth(o.original, props.adapterTipWearThresholds);
          if (!status) return null;
          const colorMap = { green: '#4caf50', amber: '#ff9800', red: '#f44336', grey: '#9e9e9e' };
          const color = colorMap[status] || '#9e9e9e';
          const wrapperClass = `${props.classes.healthIconWrapper} ${props.classes.healthIconClickable}`;
          return (
            <div
              className={wrapperClass}
              role="button"
              tabIndex={0}
              aria-label={safeTranslate(props.translate, 'adapter_health', 'Health')}
              onClick={() => this.openHealthModal(o.original)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  this.openHealthModal(o.original);
                }
              }}
            >
              <Heart size={20} color={color} />
            </div>
          );
        }
      },
      {
        id: "adapterserialno",
        Header: safeTranslate(props.translate, 'adapter_serial', 'Adapter Serial'),
        accessor: d => d.adapterserialno,
        toggle: false,
        show: true,
        display: true,
        filterable: false,
        cansearch: true,
        sortable: true,
        Cell: o => (
          <TestsRowLink
            title={o.row.adapterserialno}
            link={`/adapter/${encodeURIComponent(o.row.adapterserialno)}?from=adapters`}
          />
        )
      },
      {
        id: "moduletype",
        Header: safeTranslate(props.translate, 'adapter_module_type', 'Module Type'),
        accessor: d => d.moduletype || "",
        toggle: true,
        show: show("moduletype", true),
        display: true,
        filterable: filterable,
        cansearch: true,
        sortable: true,
        Cell: o => formatValue(o.row.moduletype)
      },
      {
        id: "adaptertypename",
        Header: safeTranslate(props.translate, 'adapter_type', 'Adapter Type'),
        accessor: d => d.adaptertypename || "",
        toggle: true,
        show: show("adaptertypename", true),
        display: true,
        filterable: filterable,
        cansearch: true,
        sortable: true,
        Cell: o => formatValue(o.row.adaptertypename)
      },
      {
        id: "linkeddeviceserialno",
        Header: safeTranslate(props.translate, 'adapter_linked_device', 'Linked Device'),
        accessor: d => d.linkeddeviceserialno || "",
        toggle: true,
        show: show("linkeddeviceserialno", true),
        display: true,
        filterable: filterable,
        cansearch: true,
        sortable: true,
        Cell: o => {
          if (!o.original.linkeddeviceid || !o.original.linkeddeviceserialno) {
            return <span>-</span>;
          }

          return (
            <TestsRowLink
              title={o.original.linkeddeviceserialno}
              link={`/device/${o.original.linkeddeviceid}?from=adapters`}
            />
          );
        }
      },
      {
        id: "firmwareversion",
        Header: safeTranslate(props.translate, 'adapter_firmware', 'Firmware'),
        accessor: d => d.firmwareversion || "",
        toggle: true,
        show: show("firmwareversion", true),
        display: true,
        filterable: filterable,
        cansearch: true,
        sortable: true,
        Cell: o => formatValue(o.row.firmwareversion)
      },
      {
        id: "calibrationduedate",
        Header: safeTranslate(props.translate, 'adapter_calibration_due', 'Calibration Due'),
        accessor: d => d.calibrationduedate || "",
        toggle: true,
        show: show("calibrationduedate", true),
        display: true,
        filterable: filterable,
        cansearch: true,
        sortable: true,
        Cell: o => formatDateTime(o.row.calibrationduedate)
      },
      {
        id: "lastresultdatetime",
        Header: safeTranslate(props.translate, 'adapter_last_result', 'Last Result'),
        accessor: d => d.lastresultdatetime || "",
        toggle: true,
        show: show("lastresultdatetime", true),
        display: true,
        filterable: filterable,
        cansearch: true,
        sortable: true,
        Cell: o => formatDateTime(o.row.lastresultdatetime)
      },
      {
        id: "testcount",
        Header: safeTranslate(props.translate, 'metrics_test_count', 'Test Count'),
        accessor: d => d.testcount,
        toggle: true,
        show: show("testcount", true),
        display: true,
        filterable: filterable,
        cansearch: true,
        sortable: true,
        Cell: o => formatValue(o.row.testcount)
      },
      {
        id: "technician",
        Header: safeTranslate(props.translate, 'metrics_technician', 'Technician'),
        accessor: d => d.technician || "",
        toggle: true,
        show: show("technician", false),
        display: true,
        filterable: filterable,
        cansearch: true,
        sortable: true,
        Cell: o => formatValue(o.row.technician)
      },
      {
        id: "tipwearcounter",
        Header: safeTranslate(props.translate, 'adapter_tip_wear_counter', 'Tip Wear Counter'),
        accessor: d => d.tipwearcounter,
        toggle: true,
        show: show("tipwearcounter", false),
        display: true,
        filterable: filterable,
        cansearch: true,
        sortable: true,
        Cell: o => formatTipWearCounter(o.row.tipwearcounter)
      }
    ];
  };

  search = term => {
    this.setState({
      searchTerm: term || "",
      selectedAdapters: [],
      selected: {}
    });
  };

  toggle = () => {
    this.setState({
      contextMenu: !this.state.contextMenu
    });
  };

  toggleColumns = column => {
    const columns = [...this.state.columns];

    columns.forEach(currentColumn => {
      if (currentColumn.id === column.id) {
        currentColumn.show = column.show;
      }
    });

    this.setState({ columns });
  };

  toggleFilters = () => {
    const nextFilterable = !this.state.filterable;
    const visibility = this.getColumnVisibility(this.state.columns);
    this.setState({
      filterable: nextFilterable,
      columns: this.createColumns(this.props, nextFilterable, visibility)
    });
  };

  openHealthModal = adapter => {
    const checks = [];
    const tipWearThreshold = getAdapterTipWearThreshold(adapter, this.props.adapterTipWearThresholds);

    // Calibration check
    if (adapter.calibrationduedate) {
      const due = getDateOnly(new Date(adapter.calibrationduedate));
      const passed = due >= getDateOnly(new Date());
      checks.push({
        name: "Calibration Due",
        passed,
        currentValue: adapter.calibrationduedate,
        expectedValue: null
      });
    } else {
      checks.push({
        name: "Calibration Due",
        passed: false,
        currentValue: null,
        expectedValue: null
      });
    }

    // Last activity check
    if (adapter.lastresultdatetime) {
      const last = new Date(adapter.lastresultdatetime);
      const daysSince = Math.floor((new Date() - last) / (1000 * 60 * 60 * 24));
      const passed = daysSince <= 90;
      checks.push({
        name: "Last Activity",
        passed,
        currentValue: daysSince,
        expectedValue: 90
      });
    } else {
      checks.push({
        name: "Last Activity",
        passed: false,
        currentValue: null,
        expectedValue: null
      });
    }

    // Tip wear check
    if (typeof adapter.tipwearcounter === "number" && adapter.tipwearcounter !== null) {
      const passed = adapter.tipwearcounter <= tipWearThreshold;
      checks.push({
        name: "Tip Wear",
        passed,
        currentValue: passed
          ? `${adapter.tipwearcounter} (threshold: ${tipWearThreshold})`
          : `${adapter.tipwearcounter} exceeds threshold of ${tipWearThreshold}`,
        expectedValue: null
      });
    }

    this.setState({
      healthCheckModal: { isOpen: true, checks }
    });
  };

  handleHealthCheckOpenChange = isOpen => {
    if (!isOpen) {
      this.setState({
        healthCheckModal: { isOpen: false, checks: null }
      });
    }
  };

  showConfirmDelete = () => {
    this.setState({ showConfirmDelete: true });
  };

  hideConfirmDelete = () => {
    this.setState({ showConfirmDelete: false });
  };

  deleteSelectedAdapter = () => {
    const selectedAdapter = this.state.selectedAdapters[0];
    if (!selectedAdapter) {
      return;
    }

    this.props.deleteAdapterAsset(selectedAdapter.id).then(() => {
      this.setState({
        showConfirmDelete: false,
        selectedAdapters: [],
        selected: {}
      });
    });
  };

  selectAdapter = (adapter, isSelected) => {
    this.setState({
      selectedAdapters: isSelected ? [adapter] : [],
      selected: isSelected ? { [adapter.id]: true } : {}
    });
  };

  getRows = () => {
    const rows = this.props.adapterAssets && this.props.adapterAssets.data ? this.props.adapterAssets.data : [];
    const term = this.state.searchTerm.trim().toLowerCase();

    if (!term) {
      return rows;
    }

    return rows.filter(row => [
      row.adapterserialno,
      row.position,
      row.moduletype,
      row.adaptertypename,
      row.linkeddeviceserialno,
      row.firmwareversion
    ].some(value => value && value.toLowerCase().includes(term)));
  };

  render() {
    const { classes, translate, viewSwitcher, adapterAssets, impersonating, softwareUpdate, profile, user } = this.props;
    const { contextMenu, columns, filterable, selectedAdapters, selected } = this.state;
    const rows = this.getRows();
    const translateText = typeof translate === "function" ? translate : value => value;
    const roles = profile && profile.roles ? profile.roles : user && user.roles;
    const deleteEnabled = ["idealadmin", "idealsupport", "clientadmin", "clientmanager"]
      .some(role => hasRole(roles, role));
    const visibleColumns = (columns || []).filter(column => column.id !== "select");
    const selectionColumn = {
      id: "select",
      Header: "",
      accessor: row => row.id,
      filterable: false,
      maxWidth: 50,
      sortable: false,
      Cell: cell => {
        const adapter = cell.original || cell.row;
        return (
          <Radio
            checked={selected[adapter.id] === true}
            onChange={event => this.selectAdapter(adapter, event.target.checked)}
            style={{ height: 24, padding: 0, width: 24 }}
            value={String(adapter.id)}
          />
        );
      }
    };
    const tableColumns = deleteEnabled ? [selectionColumn, ...visibleColumns] : visibleColumns;
    const selectedAdapterSerial = selectedAdapters[0] ? selectedAdapters[0].adapterserialno : "";
    const confirmDeleteText = translateText("adapter_delete_confirm_text", {
      adaptername: selectedAdapterSerial
    });

    let margintop = "150px";

    if (impersonating && softwareUpdate) {
      margintop = "275px";
    } else if (impersonating || softwareUpdate) {
      margintop = "215px";
    }
    const pageShellStyle = {
      paddingTop: margintop,
      boxSizing: "border-box",
      height: "100vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    };

    const $context = (
      <Context
        toggleColumns={this.toggleColumns}
        data={rows}
        translate={translateText}
        columns={visibleColumns}
      />
    );

    return (
      <div style={pageShellStyle}>
        <Title
          back={<Back link="/devices" title={translateText('general_devices')} />}
          title={translateText('adapter_assets_title')}
          toolbar={
            <AdapterToolbar
              classes={classes}
              translate={translateText}
              viewSwitcher={viewSwitcher}
              toggle={this.toggle}
              toggleFilters={this.toggleFilters}
              showDelete={!!(deleteEnabled && selectedAdapters.length === 1)}
              del={this.showConfirmDelete}
            />
          }
        />
        <Content
          ctx={$context}
          open={contextMenu}
          toggle={() => this.toggle()}
          translate={translateText}
          fillHeight
        >
          <div className={classes.bodyScroll}>
            {filterable ? (
              <SearchBar
                search={this.search}
                pager={this.state.searchPager}
                translate={translateText}
              />
            ) : null}
            {adapterAssets === null ? (
              <Paper>
                <Typography type="body1" className={classes.emptyState}>
                  {translateText('adapter_assets_loading')}
                </Typography>
              </Paper>
            ) : (
              <div className={classes.tableRegion}>
                <Table
                  key={`adapter-assets-table-${this.state.searchTerm}-${filterable}`}
                  translate={translateText}
                  filterable
                  data={rows}
                  columns={tableColumns}
                  defaultPageSize={10}
                  minRows={0}
                  style={{ border: "none", height: "100%" }}
                  className={"deviceTable"}
                  noDataText="adapter_assets_no_data"
                />
              </div>
            )}
          </div>
          <HealthCheckModal
            isOpen={this.state.healthCheckModal.isOpen}
            deviceHealth={this.state.healthCheckModal.checks}
            onOpenChange={this.handleHealthCheckOpenChange}
            translate={translateText}
          />
          <ConfirmDialog
            open={this.state.showConfirmDelete}
            title={translateText("adapter_delete_confirm_title")}
            contentText={confirmDeleteText}
            contentSecondaryText={translateText("general_confirmation_help_2")}
            okText={translateText("general_confirm")}
            cancelText={translateText("general_cancel")}
            onOk={this.deleteSelectedAdapter}
            onCancel={this.hideConfirmDelete}
          />
        </Content>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  impersonating: state.userReducer.impersonating,
  softwareUpdate: state.userReducer.softwareUpdate,
  profile: state.profileReducer.profile,
  user: state.userReducer.user,
  adapterTipWearThresholds: state.appSettingReducer.adapterTipWearThresholds
});

export default compose(
  withStyles(styles),
  connect(mapStateToProps)
)(AdapterAssetsPanel);
